import { Category } from '@modules/husleie-framework/src/enums/Category.enum';
import DateTimeOperations from '@modules/husleie-framework/src/static-classes/DateTimeOperations.class';
import { AdderType } from '@modules/husleie-framework/src/types/Adder';
import FileUpload from '@modules/husleie-framework/src/types/FileUpload.entity';
import Keychain from '@modules/husleie-framework/src/types/Keychain.entity';
import type { RentalObject } from '@modules/husleie-framework/src/types/RentalObject.entity';
import RentalObjectAddress from '@modules/husleie-framework/src/types/RentalObjectAddress.entity';
import { RentalObjectDetails } from '@modules/husleie-framework/src/types/RentalObjectDetails.entity';
import { Utility, UtilityMeter, UtilityStatusEnum, UtilityTypeEnum } from '@modules/husleie-framework/src/types/Utility';
import CommonConversionOperations from '@modules/store/common/static-classes/CommonConversionOperations.class';
import { APIProtocolVersions } from '@modules/store/common/types/APIProtocolVersions.entity';
import { RentalObjectAPIProtocolVersionEnum } from '@modules/store/rentalObject/types/RentalObjectAPI.interface';
import AddressStringOperations from '@modules/husleie-framework/src/static-classes/AddressStringOperations.class';

export default class RentalObjectConversionOperations extends CommonConversionOperations {
  static apiToInternal(apiRentalObject: any, versions?: APIProtocolVersions): RentalObject {
    let address: RentalObjectAddress;
    let rooms: undefined | AdderType[] = undefined;
    let keychain: undefined | Keychain = undefined;

    const version = this.getVersion(versions);

    if (version.startsWith('1')) {
      address = {
        street: apiRentalObject.street,
        postalCode: apiRentalObject.postalZipcode ?? apiRentalObject.postalZipcode,
        postalArea: apiRentalObject.postalArea,
        additionalAddress: apiRentalObject.street2,
        apartmentLocation: apiRentalObject.apartmentLocation,
        roomNumber: apiRentalObject.roomNumber,
        floor: apiRentalObject.floor,
        country: {
          code: apiRentalObject.countryCode,
        },
        fullAddress: AddressStringOperations.getNiceAddress(apiRentalObject),
      };
    } else {
      address = {
        street: apiRentalObject.address.street,
        postalCode: apiRentalObject.address.postalZipcode ?? apiRentalObject.address.postalCode,
        postalArea: apiRentalObject.address.postalArea,
        additionalAddress: apiRentalObject.address.street2,
        apartmentLocation: apiRentalObject.address.apartmentLocation,
        roomNumber: apiRentalObject.address.roomNumber,
        floor: apiRentalObject.address.floor,
        country: {
          code: apiRentalObject.address.countryCode,
        },
        fullAddress: AddressStringOperations.getNiceAddress(apiRentalObject.address),
      };
    }

    if (version.startsWith('1') || version.startsWith('2')) {
      rooms = this.convertAPIAdderTypeToInternal(apiRentalObject.livingAreas);
      keychain = { keys: this.convertAPIAdderTypeToInternal(apiRentalObject.keyDetail) };
    } else if (version.startsWith('3')) {
      rooms = this.convertAPIAdderTypeToInternal(apiRentalObject.rooms);
      keychain = {
        keys: this.convertAPIAdderTypeToInternal(apiRentalObject.keychain?.keys),
        images: this.apiImagesToInternal(apiRentalObject.keychain?.images),
        comment: apiRentalObject.keychain?.comment,
      };
    }

    return {
      id: apiRentalObject.id,
      category: Category.APARTMENT,
      address: address,
      electricityMeterNumber: apiRentalObject.electricityMeterNumber,
      dateOfConstruction: DateTimeOperations.apiDateToInternalDate(apiRentalObject.dateOfConstruction),
      squareMeters: apiRentalObject.squareMeters,
      furnishingStandard: apiRentalObject.furnishingStandard,
      comment: apiRentalObject.comment,

      rooms,
      furniture: this.convertAPIAdderTypeToInternal(apiRentalObject.furniture),
      storageDetail: this.convertAPIAdderTypeToInternal(apiRentalObject.storageDetail),
      keychain,
      utilities: this.convertAPIUtilitiesToInternal(apiRentalObject.utilities),
      details: this.convertAPIDetailsToInternal(apiRentalObject.details),
      additionalDocuments: this.convertAPIFileUploadToInternal(apiRentalObject.additionalDocuments),
      parkingFacilities: apiRentalObject.parkingFacilities ? apiRentalObject.parkingFacilities : undefined,
      useDefaultHouseRules: apiRentalObject.useDefaultHouseRules,
      houseRulesUploads: this.convertAPIFileUploadToInternal(apiRentalObject.houseRulesUploads),
    };
  }

  static internalToApi(rentalObject: RentalObject, versions?: APIProtocolVersions): any {
    let address: any;
    let keychain: any = undefined;
    let keyDetail: any = undefined;

    const version = this.getVersion(versions);

    if (version.startsWith('1')) {
      address = {
        street: rentalObject.address?.street,
        postalZipcode: rentalObject.address?.postalCode,
        postalArea: rentalObject.address?.postalArea,
        street2: rentalObject.address?.additionalAddress,
        apartmentLocation: rentalObject.address?.apartmentLocation,
        countryCode: rentalObject.address?.country?.code,
      };
    } else {
      address = {
        address: {
          street: rentalObject.address?.street,
          postalCode: rentalObject.address?.postalCode,
          postalArea: rentalObject.address?.postalArea,
          street2: rentalObject.address?.additionalAddress,
          apartmentLocation: rentalObject.address?.apartmentLocation,
          countryCode: rentalObject.address?.country?.code,
        },
      };
    }

    if (version.startsWith('1') || version.startsWith('2')) {
      keyDetail = this.convertAdderTypeToAPI(rentalObject.keychain?.keys);
      keychain = undefined;
    } else if (version.startsWith('3')) {
      keyDetail = undefined;
      keychain = {
        keys: this.convertAdderTypeToAPI(rentalObject.keychain?.keys),
        images: this.internalImagesToApi(rentalObject.keychain?.images),
        comment: rentalObject.keychain?.comment,
      };
    }

    const apiRentalObject: any = {
      belongsToBuildingId: rentalObject.belongsToBuildingId,
      category: rentalObject.category,

      ...address,

      electricityMeterNumber: rentalObject.electricityMeterNumber,
      dateOfConstruction: DateTimeOperations.internalToApiDate(rentalObject.dateOfConstruction),
      squareMeters: rentalObject.squareMeters ? rentalObject.squareMeters : undefined,
      furnishingStandard: rentalObject.furnishingStandard,
      comment: rentalObject.comment,

      furniture: this.convertAdderTypeToAPI(rentalObject.furniture),
      storageDetail: this.convertAdderTypeToAPI(rentalObject.storageDetail),
      keyDetail,
      keychain,
      utilities: this.convertUtilitiesToAPI(rentalObject.utilities),
      details: this.convertDetailsToAPI(rentalObject.details),
      additionalDocuments: this.convertFileUploadToAPI(rentalObject.additionalDocuments),
      parkingFacilities: rentalObject.parkingFacilities ? rentalObject.parkingFacilities : undefined,
      useDefaultHouseRules: rentalObject.useDefaultHouseRules,
      houseRulesUploads: this.convertFileUploadToAPI(rentalObject.houseRulesUploads),
    };

    if (version.startsWith('2')) {
      apiRentalObject.livingAreas = this.convertAdderTypeToAPI(rentalObject.rooms);
    } else {
      apiRentalObject.rooms = this.convertAdderTypeToAPI(rentalObject.rooms);
    }

    return apiRentalObject;
  }

  private static convertUtilitiesToAPI(inputs: undefined | Utility[]): undefined | any {
    if (inputs) {
      const apiInputs: any[] = inputs.map((utility: Utility) => {
        if (utility.state === UtilityStatusEnum.RELEVANT) {
          return {
            type: utility.type,
            state: utility.state,
            meter: {
              number: utility.meter?.number,
              reading: {
                date: DateTimeOperations.internalToApiDate(utility.meter?.reading?.date),
                value: utility.meter?.reading?.value,
                images: this.internalImagesToApi(utility.meter?.reading?.images),
                comment: utility.meter?.reading?.comment,
              },
            },
          };
        } else {
          return {
            type: utility.type,
            state: utility.state,
          };
        }
      });

      let electricity = apiInputs.find((utility: any) => utility.type === UtilityTypeEnum.ELECTRICITY);
      let hotWater = apiInputs.find((utility: any) => utility.type === UtilityTypeEnum.HOT_WATER);
      let coldWater = apiInputs.find((utility: any) => utility.type === UtilityTypeEnum.COLD_WATER);
      let gas = apiInputs.find((utility: any) => utility.type === UtilityTypeEnum.GAS);
      let heating = apiInputs.find((utility: any) => utility.type === UtilityTypeEnum.HEATING);

      if (!electricity) {
        electricity = { state: UtilityStatusEnum.NOT_RELEVANT };
      } else {
        electricity.type = undefined;
      }
      if (!hotWater) {
        hotWater = { state: UtilityStatusEnum.NOT_RELEVANT };
      } else {
        hotWater.type = undefined;
      }
      if (!coldWater) {
        coldWater = { state: UtilityStatusEnum.NOT_RELEVANT };
      } else {
        coldWater.type = undefined;
      }
      if (!gas) {
        gas = { state: UtilityStatusEnum.NOT_RELEVANT };
      } else {
        gas.type = undefined;
      }
      if (!heating) {
        heating = { state: UtilityStatusEnum.NOT_RELEVANT };
      } else {
        heating.type = undefined;
      }

      return { electricity, hotWater, coldWater, gas, heating };
    }
    return undefined;
  }

  private static convertAPIUtilitiesToInternal(inputs: undefined | any): Utility[] {
    if (inputs) {
      const returnList: Utility[] = [
        { type: UtilityTypeEnum.ELECTRICITY, state: UtilityStatusEnum.NOT_RELEVANT, ...inputs.electricity },
        { type: UtilityTypeEnum.HOT_WATER, state: UtilityStatusEnum.NOT_RELEVANT, ...inputs.hotWater },
        { type: UtilityTypeEnum.COLD_WATER, state: UtilityStatusEnum.NOT_RELEVANT, ...inputs.coldWater },
        { type: UtilityTypeEnum.GAS, state: UtilityStatusEnum.NOT_RELEVANT, ...inputs.gas },
        { type: UtilityTypeEnum.HEATING, state: UtilityStatusEnum.NOT_RELEVANT, ...inputs.heating },
      ];

      // sanity checks
      return returnList.map((entry: any) => {
        let utility: Utility;
        if (!entry.meter) {
          utility = { ...entry, meter: { reading: { images: [] } } };
        } else if (!entry.meter.reading) {
          const meter: UtilityMeter = { ...entry.meter, reading: { images: [] } };
          utility = { ...entry, meter };
        } else {
          utility = { ...entry };
        }
        utility.meter!.reading!.images = this.apiImagesToInternal(utility.meter?.reading?.images);
        utility.meter!.reading!.date = DateTimeOperations.apiDateToInternalDate(entry.meter?.reading?.date);
        return utility;
      });
    }
    return [];
  }

  private static convertAdderTypeToAPI(inputs: undefined | AdderType[]): undefined | any[] {
    if (inputs) {
      return inputs.map(input => {
        return {
          name: input.name,
          comment: input.comment,
          quantity: input.quantity,
          images: this.internalImagesToApi(input.images),
        };
      });
    }
    return undefined;
  }

  private static convertAPIAdderTypeToInternal(inputs: undefined | any[]): AdderType[] {
    if (inputs) {
      return inputs.map(input => {
        return {
          name: input.name,
          comment: input.comment,
          quantity: input.quantity ? Number(input.quantity) : undefined,
          images: this.apiImagesToInternal(input.images),
        };
      });
    }
    return [];
  }

  private static convertFileUploadToAPI(inputs: undefined | FileUpload[]): undefined | any[] {
    if (inputs) {
      return inputs.map(input => {
        return {
          file: input.file,
          filename: input.filename,
          token: input.token,
        };
      });
    }
    return undefined;
  }

  private static convertAPIFileUploadToInternal(inputs: undefined | any[]): FileUpload[] {
    if (inputs) {
      return inputs.map(input => {
        return {
          file: input.file,
          filename: input.filename,
          token: input.token,
        };
      });
    }
    return [];
  }

  private static convertDetailsToAPI(rentalObjectDetails: undefined | RentalObjectDetails): undefined | any {
    if (rentalObjectDetails) {
      return {
        moveInDate: DateTimeOperations.internalToApiDate(rentalObjectDetails?.moveInDate),
        moveOutDate: DateTimeOperations.internalToApiDate(rentalObjectDetails?.moveOutDate),
        amountOfResidents: rentalObjectDetails?.amountOfResidents,
      };
    } else {
      return undefined;
    }
  }

  private static convertAPIDetailsToInternal(apiRentalObjectDetails: any): RentalObjectDetails {
    if (apiRentalObjectDetails) {
      return {
        moveInDate: DateTimeOperations.apiDateToInternalDate(apiRentalObjectDetails?.moveInDate),
        moveOutDate: DateTimeOperations.apiDateToInternalDate(apiRentalObjectDetails?.moveOutDate),
        amountOfResidents: apiRentalObjectDetails?.amountOfResidents,
      };
    } else return {};
  }

  private static getVersion(versions?: APIProtocolVersions) {
    return versions && versions.rentalObjectAPIProtocolVersion
      ? versions.rentalObjectAPIProtocolVersion
      : RentalObjectAPIProtocolVersionEnum.v3_0_0;
  }
}
